import {
  Box,
  Button,
  Flex,
  Grid,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import dayjs from 'dayjs';
import { useUtcOffsetStore } from '@Models/useUtcOffsetModel';
import Dropdown from '@Components/Common/Dropdown';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export const MonthPicker = ({
  data,
  setFromTo,
  onClose,
}: {
  data: {
    from: string;
    to: string;
  };
  setFromTo: (from: string, to: string) => void;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { offset } = useUtcOffsetStore();
  const [from, setFrom] = useState(() => {
    const time = dayjs.tz(data.from, '+00:00');
    const year = time.year();
    const month = time.month() + 1;
    return {
      year,
      month,
    };
  });

  const [to, setTo] = useState(() => {
    const time = dayjs.tz(data.to, '+00:00');
    const year = time.year();
    const month = time.month() + 1;
    return {
      year,
      month,
    };
  });

  const setFromAndAdjustTo = (fromYear: number, fromMonth: number) => {
    setFrom({
      year: fromYear,
      month: fromMonth,
    });
    const diff = dayjs(to.year + '-' + to.month).diff(
      dayjs(fromYear + '-' + fromMonth),
      'month'
    );
    if (diff >= 12) {
      const endTime = dayjs(fromYear + '-' + fromMonth).add(11, 'month');
      setTo({
        year: endTime.year(),
        month: endTime.month() + 1,
      });
    } else if (diff < 0) {
      setTo({
        year: fromYear,
        month: fromMonth,
      });
    }
  };

  const maxYear = dayjs().year();
  const minYear = maxYear - 1;

  //TODO: exclude current month, current month can not aggress log data
  const maxMonth = dayjs().utc().utcOffset(offset).month();

  return (
    <Box
      backgroundColor={colorStyle.background.tertiary}
      position="absolute"
      left="100%"
      top={260}
      px="3x"
      py="3x"
      borderLeft={1}
      borderColor={colorStyle.divider}
    >
      <Flex alignItems="center" columnGap="3x" mb="6x">
        <Text fontSize="md" lineHeight="md">
          {t('reportCommon.period.label.customPeriod')}
        </Text>
      </Flex>
      <Box maxHeight="0px" overflow="hide" position="relative">
        <MenuList
          position="absolute"
          left="-9999px !important"
          width="max-content"
        >
          {getMonthsArr(1, 12).map((v) => (
            <MenuItem key={v}>{t('common.month.' + v)}</MenuItem>
          ))}
        </MenuList>
      </Box>
      <Grid templateColumns="auto 1fr" alignItems="center" rowGap="3x" mb="3x">
        <Text minWidth="16x">{t('common.timeRange.from')}:</Text>
        <Flex align="center" gap="8px">
          <Dropdown
            options={[minYear + '', maxYear + '']}
            value={from.year + ''}
            onChange={(v) => {
              if (Number(v) === maxYear && Number(from.month) > maxMonth) {
                setFromAndAdjustTo(Number(v), maxMonth);
              } else {
                setFromAndAdjustTo(Number(v), from.month);
              }
            }}
          />
          <Dropdown
            minWidth={125}
            menuWidth="max-content"
            menuHeight="180px" //5 items height
            options={
              from.year === minYear
                ? getMonthsArr(1, 12)
                : getMonthsArr(1, maxMonth)
            }
            renderOption={(v) => t('common.month.' + v) as string}
            value={from.month + ''}
            onChange={(v) => {
              setFromAndAdjustTo(from.year, Number(v));
            }}
          />
        </Flex>
        <Text minWidth="16x">{t('common.timeRange.to')}:</Text>
        <Flex align="center" gap="8px">
          <Menu>
            <MenuButton variant="secondary">{to.year}</MenuButton>
            <MenuList overflow="auto" width="max-content">
              <MenuItem
                value={minYear}
                disabled={from.year === maxYear}
                onClick={(e: any) => {
                  setTo({ ...to, year: Number(e.target.value) });
                }}
              >
                {minYear}
              </MenuItem>
              <MenuItem
                value={maxYear}
                disabled={from.year === minYear && from.month === 1}
                onClick={(e: any) => {
                  if (to.month > maxMonth) {
                    setTo({
                      year: Number(e.target.value),
                      month: maxMonth,
                    });
                  } else {
                    setTo({ ...to, year: Number(e.target.value) });
                  }
                }}
              >
                {maxYear}
              </MenuItem>
            </MenuList>
          </Menu>

          <Menu>
            <MenuButton variant="secondary" minWidth={125}>
              {t('common.month.' + to.month)}
            </MenuButton>
            <MenuList maxHeight={180} overflow="auto" width="max-content">
              {getMonthsArr(1, to.year === minYear ? 12 : maxMonth).map((v) => {
                const diff = dayjs(to.year + '-' + v).diff(
                  dayjs(from.year + '-' + from.month),
                  'month'
                );
                const disabled = diff >= 12 || diff < 0;
                return (
                  <MenuItem
                    key={v}
                    value={v}
                    disabled={disabled}
                    onClick={(e: any) => {
                      setTo({
                        ...to,
                        month: Number(e.target.value),
                      });
                    }}
                  >
                    {t('common.month.' + v)}
                  </MenuItem>
                );
              })}
            </MenuList>
          </Menu>
        </Flex>
      </Grid>
      <Flex justifyContent="flex-end">
        <Grid templateColumns="1fr 1fr" columnGap="2x">
          <Button
            variant="primary"
            onClick={() => {
              setFromTo(
                dayjs
                  .tz(from.year + '-' + from.month, '+00:00')
                  .format('YYYY-MM'),
                dayjs.tz(to.year + '-' + to.month, '+00:00').format('YYYY-MM')
              );
              onClose();
            }}
          >
            {t('common.button.apply')}
          </Button>
          <Button variant="secondary" onClick={onClose}>
            {t('common.button.cancel')}
          </Button>
        </Grid>
      </Flex>
    </Box>
  );
};

const getMonthsArr = (start: number, end: number) => {
  return Array.from({ length: end - start + 1 }, (_, i) => i + start + '');
};
